import { message } from "antd";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { getAxiosError } from "../../../../../../api";
import type { AppDispatch } from "../../../../../../redux/store";
import { DecimalUtils } from "../../../../../libs/utils";
import { type CommercialPaymentPlan, type PlanFieldItem } from "../../../../BookingForm/components/Commercial/add-plan";
import { useSaveBillingSchedule } from "../../../../BookingForm/components/Commercial/add-plan/api";
import type { SaveBillingScheduleRequest } from "../../../../BookingForm/components/Commercial/add-plan/api/useSaveBillingSchedule";
import { buildCommercialBillingScheduleBreakdownPayload } from "../../../../BookingForm/components/Commercial/add-plan/buildCommercialBillingScheduleBreakdownPayload";
import { getBookingDetail } from "../../../../BookingForm/ducks/actions";
import type { PaymentPlansStepFormValues } from "./types";

type UseSavePaymentPlansProps = {
  dispatch: AppDispatch;
  leaseId?: string;
  onSuccess?: () => void;
};

const mapPlansToCommercialPaymentPlanPayload = (plans: PlanFieldItem[]): CommercialPaymentPlan[] =>
  plans.map((plan, index) => ({
    annualRent: DecimalUtils.toNumber(plan?.annualRent),
    installments: Number(plan?.installments?.value ?? 0),
    noOfYear: plan?.noOfYear ?? index + 1,
    rentType: plan?.rentType?.value ?? "",
    ...(plan?.rentValue ? { rentValue: DecimalUtils.toNumber(plan?.rentValue) } : {}),
    securityDeposit: DecimalUtils.toNumber(plan?.securityDeposit),
    ...(plan?.torPeriod?.value != null ? { torPeriod: Number(plan.torPeriod.value) } : {}),
  }));

const buildSaveBillingSchedulePayload = (leaseId: string, plans: PlanFieldItem[]): SaveBillingScheduleRequest => ({
  leaseId,
  commercialPaymentPlan: mapPlansToCommercialPaymentPlanPayload(plans),
  commercialBillingScheduleBreakdown: buildCommercialBillingScheduleBreakdownPayload(plans),
});

export const useSavePaymentPlans = (props: UseSavePaymentPlansProps) => {
  const { dispatch, leaseId, onSuccess } = props;
  const { getValues } = useFormContext<PaymentPlansStepFormValues>();

  const { saveBillingSchedule, isPendingSaveBillingSchedule } = useSaveBillingSchedule({
    onError: (error) => message.error(getAxiosError(error, "Failed to save payment plans")),
  });

  const savePaymentPlans = useCallback(async () => {
    if (!leaseId) {
      message.error("Unable to save payment plans because lease details are missing");
      return false;
    }

    const currentPlans = getValues("plans") ?? [];

    // Every year card must have a generated breakdown — partial coverage must not proceed.
    for (let index = 0; index < currentPlans.length; index += 1) {
      const plan = currentPlans[index];
      const yearLabel = `Year ${plan?.noOfYear ?? index + 1}`;

      if (!plan?.billingScheduleRows?.length) {
        message.error(`${yearLabel}: Please generate the billing schedule breakdown before proceeding`);
        return false;
      }

      const hasServiceChargeInput =
        plan?.serviceCharge?.rentType != null ||
        plan?.serviceCharge?.installments != null ||
        plan?.serviceCharge?.annualRent != null ||
        plan?.serviceCharge?.chargeValue != null;

      if (hasServiceChargeInput && !plan?.serviceChargeSchedule?.length) {
        message.error(`${yearLabel}: Please generate the service charge breakdown before proceeding`);
        return false;
      }
    }

    const payload = buildSaveBillingSchedulePayload(leaseId, currentPlans);

    if (payload.commercialBillingScheduleBreakdown.length !== currentPlans.length) {
      message.error("Please generate the billing schedule breakdown for all renewal years before proceeding");
      return false;
    }

    try {
      const response = await saveBillingSchedule(payload);
      await dispatch(getBookingDetail(leaseId));
      onSuccess?.();
      if (response?.message) message.success(response.message);
      return true;
    } catch {
      return false;
    }
  }, [dispatch, getValues, leaseId, onSuccess, saveBillingSchedule]);

  return {
    savePaymentPlans,
    isPendingSavePaymentPlans: isPendingSaveBillingSchedule,
  };
};
